import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, ChevronRight, RotateCcw, Trophy, Loader2 } from 'lucide-react';
import { useApi } from '../hooks/useApi';

interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
}

interface QuizData {
  id: string;
  lessonId: string;
  title: string | null;
  questions: QuizQuestion[];
  questionCount: number;
  passingScore: number;
}

interface QuizResult {
  questionId: string;
  correct: boolean;
  selectedIndex: number;
  correctIndex: number;
  explanation?: string;
}

interface SubmitResponse {
  success: boolean;
  attempt: {
    id: string;
    score: number;
    passed: boolean;
    correctCount: number;
    totalQuestions: number;
    passingScore: number;
  };
  results: QuizResult[];
}

interface QuizProps {
  lessonId: string;
  onComplete?: (passed: boolean, score: number) => void;
}

export default function Quiz({ lessonId, onComplete }: QuizProps) {
  const { fetchApi } = useApi();
  const [quiz, setQuiz] = useState<QuizData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<SubmitResponse | null>(null);

  useEffect(() => {
    loadQuiz();
  }, [lessonId]);

  async function loadQuiz() {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchApi<{ quiz: QuizData | null }>(`/api/quizzes/lesson/${lessonId}`);
      setQuiz(data.quiz);
    } catch (err) {
      setError('Failed to load quiz');
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit() {
    if (!quiz) return;

    // Check all questions answered
    const unanswered = quiz.questions.filter(q => answers[q.id] === undefined);
    if (unanswered.length > 0) {
      setError(`Please answer all questions (${unanswered.length} remaining)`);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetchApi<SubmitResponse>(`/api/quizzes/${quiz.id}/submit`, {
        method: 'POST',
        body: JSON.stringify({ answers })
      });
      setResults(response);
      onComplete?.(response.attempt.passed, response.attempt.score);
    } catch (err) {
      setError('Failed to submit quiz');
    } finally {
      setSubmitting(false);
    }
  }

  function handleRetry() {
    setAnswers({});
    setResults(null);
    setCurrentQuestion(0);
  }

  function selectAnswer(questionId: string, optionIndex: number) {
    if (results) return; // Don't allow changes after submission
    setAnswers(prev => ({ ...prev, [questionId]: optionIndex }));
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" role="status" aria-label="Loading quiz">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" aria-hidden="true" />
        <span className="sr-only">Loading quiz...</span>
      </div>
    );
  }

  if (!quiz) {
    return null; // No quiz for this lesson
  }

  if (error && !results) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
        {error}
        <button
          onClick={loadQuiz}
          className="ml-4 text-sm underline hover:no-underline"
        >
          Try again
        </button>
      </div>
    );
  }

  // Results view
  if (results) {
    const { attempt } = results;
    const passed = attempt.passed;

    return (
      <div className="bg-gray-800/50 rounded-lg p-6">
        <div className="text-center mb-8">
          <div className={`
            inline-flex items-center justify-center w-20 h-20 rounded-full mb-4
            ${passed ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}
          `}>
            {passed ? (
              <Trophy className="w-10 h-10" />
            ) : (
              <XCircle className="w-10 h-10" />
            )}
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">
            {passed ? 'Congratulations!' : 'Keep Practicing'}
          </h3>
          <p className="text-gray-400">
            You scored <span className="text-white font-bold">{attempt.score}%</span>
            {' '}({attempt.correctCount} of {attempt.totalQuestions} correct)
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Passing score: {attempt.passingScore}%
          </p>
        </div>

        {/* Show results for each question */}
        <div className="space-y-4 mb-6">
          {quiz.questions.map((question, index) => {
            const result = results.results.find(r => r.questionId === question.id);
            if (!result) return null;

            return (
              <div
                key={question.id}
                className={`
                  p-4 rounded-lg border
                  ${result.correct
                    ? 'bg-green-500/10 border-green-500/30'
                    : 'bg-red-500/10 border-red-500/30'
                  }
                `}
              >
                <div className="flex items-start gap-3">
                  {result.correct ? (
                    <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <p className="text-white font-medium mb-2">
                      {index + 1}. {question.question}
                    </p>
                    <div className="text-sm space-y-1">
                      {!result.correct && (
                        <>
                          <p className="text-red-400">
                            Your answer: {question.options[result.selectedIndex] || 'No answer'}
                          </p>
                          <p className="text-green-400">
                            Correct answer: {question.options[result.correctIndex]}
                          </p>
                        </>
                      )}
                      {result.explanation && (
                        <p className="text-gray-400 mt-2 italic">
                          {result.explanation}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {!passed && (
          <button
            onClick={handleRetry}
            className="w-full flex items-center justify-center gap-2 bg-accent-blue hover:bg-blue-500 text-slate-900 font-medium py-3 px-6 rounded-lg transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Try Again
          </button>
        )}
      </div>
    );
  }

  // Quiz taking view
  const question = quiz.questions[currentQuestion];
  const progress = ((currentQuestion + 1) / quiz.questions.length) * 100;
  const allAnswered = quiz.questions.every(q => answers[q.id] !== undefined);

  return (
    <div className="bg-gray-800/50 rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-white">
          {quiz.title || 'Quiz'}
        </h3>
        <span className="text-sm text-gray-400">
          {currentQuestion + 1} of {quiz.questions.length}
        </span>
      </div>

      {/* Progress bar */}
      <div
        className="h-2 bg-gray-700 rounded-full mb-8 overflow-hidden"
        role="progressbar"
        aria-valuenow={currentQuestion + 1}
        aria-valuemin={1}
        aria-valuemax={quiz.questions.length}
        aria-label={`Question ${currentQuestion + 1} of ${quiz.questions.length}`}
      >
        <div
          className="h-full bg-accent-blue transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Question */}
      <div className="mb-6" role="group" aria-labelledby={`question-${question.id}`}>
        <p id={`question-${question.id}`} className="text-lg text-white mb-4">{question.question}</p>

        <div className="space-y-3" role="radiogroup" aria-labelledby={`question-${question.id}`}>
          {question.options.map((option, index) => {
            const isSelected = answers[question.id] === index;

            return (
              <button
                key={index}
                onClick={() => selectAnswer(question.id, index)}
                role="radio"
                aria-checked={isSelected}
                aria-label={`Option ${String.fromCharCode(65 + index)}: ${option}`}
                className={`
                  w-full text-left p-4 rounded-lg border transition-all
                  ${isSelected
                    ? 'bg-accent-blue/20 border-accent-blue text-white'
                    : 'bg-gray-700/50 border-gray-600 text-gray-300 hover:border-gray-500'
                  }
                `}
              >
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full border mr-3 text-sm" aria-hidden="true">
                  {String.fromCharCode(65 + index)}
                </span>
                {option}
              </button>
            );
          })}
        </div>
      </div>

      {error && (
        <p className="text-red-400 text-sm mb-4">{error}</p>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
          disabled={currentQuestion === 0}
          className="px-4 py-2 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Previous
        </button>

        {currentQuestion < quiz.questions.length - 1 ? (
          <button
            onClick={() => setCurrentQuestion(currentQuestion + 1)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={!allAnswered || submitting}
            className="flex items-center gap-2 px-6 py-2 bg-accent-blue hover:bg-blue-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-slate-900 rounded-lg transition-colors"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Quiz'
            )}
          </button>
        )}
      </div>

      {/* Question navigation dots */}
      <nav className="flex justify-center gap-2 mt-6" aria-label="Quiz question navigation">
        {quiz.questions.map((q, index) => {
          const isAnswered = answers[q.id] !== undefined;
          const isCurrent = index === currentQuestion;

          return (
            <button
              key={q.id}
              onClick={() => setCurrentQuestion(index)}
              aria-label={`Go to question ${index + 1}${isAnswered ? ', answered' : ', not answered'}${isCurrent ? ', current' : ''}`}
              aria-current={isCurrent ? 'step' : undefined}
              className={`
                w-3 h-3 rounded-full transition-all
                ${isCurrent
                  ? 'bg-accent-blue scale-125'
                  : isAnswered
                    ? 'bg-green-500'
                    : 'bg-gray-600 hover:bg-gray-500'
                }
              `}
              title={`Question ${index + 1}${isAnswered ? ' (answered)' : ''}`}
            />
          );
        })}
      </nav>
    </div>
  );
}
